/**
 * Loader for the DTCG token manifest `src/editor-v2/tokens/wm.tokens.json` —
 * the single source of token defaults (Spec A1-A3,
 * docs/superpowers/specs/2026-08-17-token-manifest-vocabulary-design.md).
 * Converts the DTCG tree into the flat CSS-value view every consumer needs:
 * cssVar -> serialized CSS string, plus the small per-group lookup tables
 * that ciTokens.js (and later the emitter/SCSS parity guards) consume.
 *
 * Serialization rules (Spec A3, byte-parity to today's literals):
 * 1. dimension 0 -> '0' (no unit); otherwise '<value><unit>', unnormalized.
 * 2. shadow -> '<offsetX> <offsetY> <blur> <color>'; spread only when != 0.
 * 3. color with alpha -> legacy 'rgba(r, g, b, a)' syntax with spaces after
 *    commas; without alpha -> hex unchanged.
 * 4. an `$extensions["io.webmag"].cssValue` always wins over serialization.
 *
 * Plain JS (not TS, persist-once spec 2026-08-18-token-css-persist-once
 * Task 2): ciTokens.js's generateTokenCss() must resolve inside the Vue-2
 * webpack build (ProjectSettings.vue/FolderSettings.vue import it via
 * tokenCssPersist.js), which cannot bundle .ts files — precedent:
 * cssEmitter.js, elementSchemas.js. Types live in wmTokensManifest.d.ts.
 */
import manifest from '../tokens/wm.tokens.json';

// Documented CSS-var-prefix exceptions and component-token patterns — data
// constants, not hardcoded in guard tests (Spec A2, Review-Kleinigkeit).
export const CSS_VAR_PREFIX_EXCEPTIONS = ['--nav-height'];
export const COMPONENT_TOKEN_PATTERNS = [/^--wm-btn-/, /^--v2e-/];

const ALIAS_RE = /^\{([^{}]+)\}$/;

function isTokenNode(node) {
  return !!node && typeof node === 'object' && '$value' in node;
}

function serializeDimension(d) {
  return d.value === 0 ? '0' : `${d.value}${d.unit}`; // rule 1
}

function serializeColor(c) {
  if (c.alpha === undefined) return c.hex; // rule 3
  const [r, g, b] = c.components.map((x) => Math.round(x * 255));
  return `rgba(${r}, ${g}, ${b}, ${c.alpha})`;
}

function serializeShadow(s) {
  const parts = [serializeDimension(s.offsetX), serializeDimension(s.offsetY), serializeDimension(s.blur)];
  if (s.spread && s.spread.value !== 0) parts.push(serializeDimension(s.spread)); // rule 2
  parts.push(serializeColor(s.color));
  return parts.join(' ');
}

function serializeByType(type, value) {
  switch (type) {
    case 'dimension':
      return serializeDimension(value);
    case 'color':
      return serializeColor(value);
    case 'shadow':
      return serializeShadow(value);
    default:
      throw new Error(`wm.tokens.json: unknown or missing $type ('${String(type)}')`);
  }
}

/** Recursively collects every `$value`-bearing leaf into `out`, keyed by dot path. $type inherits down the tree (DTCG rule). */
function collectRawLeaves(node, path, inheritedType, out) {
  if (node === null || typeof node !== 'object') return;
  const obj = node;
  const type = obj.$type ?? inheritedType;
  if (isTokenNode(obj)) {
    out.set(path.join('.'), { node: obj, type });
    return;
  }
  Object.keys(obj).forEach((key) => {
    if (key.startsWith('$')) return;
    collectRawLeaves(obj[key], [...path, key], type, out);
  });
}

/** Resolves a token's `$value`, following `{path.to.token}` aliases (Review-Add-on: DTCG alias syntax for radius overrides). */
function resolveValue(path, leaves, visiting) {
  const leaf = leaves.get(path);
  if (!leaf) throw new Error(`wm.tokens.json: unknown token path '${path}'`);
  const raw = leaf.node.$value;
  if (typeof raw === 'string') {
    const match = ALIAS_RE.exec(raw);
    if (match) {
      const targetPath = match[1];
      if (visiting.has(path)) throw new Error(`wm.tokens.json: circular alias at '${path}'`);
      visiting.add(path);
      const resolved = resolveValue(targetPath, leaves, visiting);
      visiting.delete(path);
      // The alias inherits the referencing token's own $type when it has one
      // (both point at the same conceptual shape in this manifest).
      return { value: resolved.value, type: leaf.type ?? resolved.type };
    }
  }
  return { value: raw, type: leaf.type };
}

let cachedTokens = null;

function allFlatTokens() {
  if (cachedTokens) return cachedTokens;

  const leaves = new Map();
  const manifestRoot = manifest;
  Object.keys(manifestRoot).forEach((groupKey) => {
    if (groupKey.startsWith('$')) return; // e.g. root "$schema"
    collectRawLeaves(manifestRoot[groupKey], [groupKey], undefined, leaves);
  });

  const out = [];
  leaves.forEach((leaf, path) => {
    const ext = leaf.node.$extensions?.['io.webmag'];
    if (!ext) throw new Error(`wm.tokens.json: token '${path}' is missing $extensions["io.webmag"]`);
    const resolved = resolveValue(path, leaves, new Set());
    const cssValue = ext.cssValue ?? serializeByType(resolved.type, resolved.value);
    out.push({
      path,
      cssVar: ext.cssVar,
      cssValue,
      cssValueFallback: ext.cssValueFallback,
      fallbackPx: ext.fallbackPx,
      source: ext.source,
      declaredIn: ext.declaredIn,
      description: leaf.node.$description ?? '',
      descriptionEn: ext.descriptionEn ?? '',
      syntheticValue: ext.syntheticValue,
      catalogOnly: ext.catalogOnly,
      factor: ext.factor,
    });
  });
  cachedTokens = out;
  return out;
}

function byPathPrefix(prefix) {
  return allFlatTokens().filter((t) => t.path.startsWith(prefix));
}

function lastSegment(path) {
  return path.split('.').pop();
}

/** Every token, flattened — the source for guards and the vocabulary export (Task 2/3/6). */
export function getAllTokens() {
  return allFlatTokens().map(({ path: _path, factor: _factor, ...rest }) => rest);
}

/**
 * Same rows as `getAllTokens()`, with the manifest's own group path
 * attached as `manifestPath`. Final-Review Minor M4: several manifest
 * entries legitimately share one `cssVar` (`--wm-space` is the base value
 * AND all three presets — compact/normal/airy); without the path, those
 * rows are indistinguishable to a consumer keying off `cssVar` alone. Kept
 * as a SEPARATE export rather than changing `getAllTokens()` itself, so
 * every existing consumer/guard that relies on that function's exact
 * `WmManifestToken` shape (no `path`) is unaffected.
 */
export function getAllTokensWithPath() {
  return allFlatTokens().map(({ path, factor: _factor, ...rest }) => ({ ...rest, manifestPath: path }));
}

/** Looks up the serialized CSS value for a given `--wm-*` (or exempted) custom property name. */
export function getManifestCssValue(cssVar) {
  const found = allFlatTokens().find((t) => t.cssVar === cssVar);
  return found ? found.cssValue : null;
}

/** Replaces ciTokens.js' local SPACING table. */
export function getSpacePresets() {
  const out = {};
  byPathPrefix('space.presets.').forEach((t) => { out[lastSegment(t.path)] = t.cssValue; });
  return out;
}

/** Replaces ciTokens.js' local SPACE_STEP_FACTOR table. */
export function getSpaceStepFactors() {
  const out = {};
  byPathPrefix('space.scale.').forEach((t) => { out[lastSegment(t.path)] = t.factor; });
  return out;
}

/** Static px fallbacks for the spacing scale (factor * 4px at the 0.25rem base). */
export function getSpaceFallbackPx() {
  const out = {};
  byPathPrefix('space.scale.').forEach((t) => { out[lastSegment(t.path)] = t.fallbackPx; });
  return out;
}

/** Replaces both copies of ciTokens.js' local CONTENT_WIDTH_PX table. */
export function getContentWidths() {
  const out = {};
  byPathPrefix('content.').forEach((t) => { out[lastSegment(t.path)] = t.cssValue; });
  return out;
}

/** Replaces ciTokens.js' local CONTENT_STEP_FACTOR table. */
export function getContentStepFactors() {
  const out = {};
  byPathPrefix('content.').forEach((t) => { out[lastSegment(t.path)] = t.factor; });
  return out;
}

/** Replaces ciTokens.js' local RADIUS table. */
export function getRadiusScale() {
  const out = {};
  byPathPrefix('radius.scale.').forEach((t) => { out[lastSegment(t.path)] = t.cssValue; });
  return out;
}

/** Replaces ciTokens.js' local SHADOW table. */
export function getShadowScale() {
  const out = {};
  byPathPrefix('shadow.scale.').forEach((t) => { out[lastSegment(t.path)] = t.cssValue; });
  return out;
}
